import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Science, BarChart, Assignment } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Breadcrumb } from '../../../components/layout/Breadcrumb';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../components/shared/ConfirmDialog';
import { TestNotFound } from '../../../components/shared/TestNotFound';
import { ROUTES } from '../../../constants/routes';
import { usePermissions } from '../../../hooks/usePermissions';
import { useUnsavedChangesWarning } from '../../../hooks/useUnsavedChangesWarning';
import { useRegisterUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { getPsdData, savePsdData } from '../../../services/psdService';
import { downloadReport } from '../../../services/reportService';
import type {
  PSDContext,
  PSDGeneral,
  PSDHydroDetail,
  PSDNavigation,
  PSDSieveDetail,
} from '../../../types/psd';
import { TestFooterActions } from '../components/TestFooterActions';
import { TestActionBar } from '../components/TestActionBar';
import { useReportPreview } from '../../../components/reportPreview/useReportPreview';
import { EMPTY_PSD_GENERAL } from '../config/psdFields';
import { PSDParametersTab } from './PSDParametersTab';
import { PSDResultEntry } from './PSDResultEntry';
import { PSDAnalysis } from './PSDAnalysis';

const TAB_DEFS = [
  { label: 'Test Parameters', icon: <Science sx={{ fontSize: 18 }} /> },
  { label: 'Result Entry', icon: <Assignment sx={{ fontSize: 18 }} /> },
  { label: 'Analysis & Plots', icon: <BarChart sx={{ fontSize: 18 }} /> },
];

const EMPTY_CONTEXT: PSDContext = {
  locationName: '', sampleReference: '', specimenRef: null, specimenDepth: null,
  depth: 0, testName: '', workOrderId: '', projectId: '', projectName: '',
  clientName: '', siteName: '', sampleType: '', siteKey: '',
};

const EMPTY_NAV: PSDNavigation = {
  previousWorkOrderDetailKey: null, nextWorkOrderDetailKey: null,
  currentIndex: 0, totalSamples: 0,
};

export const PSDWorkflowPage = () => {
  const { projectId, workOrderId, workOrderDetailKey } = useParams<{
    projectId: string; workOrderId: string; workOrderDetailKey: string;
  }>();
  const navigate = useNavigate();
  const { openPreview, openHistory, previewDialog } = useReportPreview(workOrderDetailKey);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const { canEditTestData, canSaveTestData } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTabState] = useState(() => {
    const t = parseInt(searchParams.get('tab') || '0', 10);
    return t >= 0 && t < TAB_DEFS.length ? t : 0;
  });
  const setActiveTab = useCallback((tab: number) => {
    setActiveTabState(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 0) next.delete('tab');
      else next.set('tab', String(tab));
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [general, setGeneral] = useState<PSDGeneral>(EMPTY_PSD_GENERAL);
  const [sieveDetails, setSieveDetails] = useState<PSDSieveDetail[]>([]);
  const [hydroDetails, setHydroDetails] = useState<PSDHydroDetail[]>([]);
  const [context, setContext] = useState<PSDContext>(EMPTY_CONTEXT);
  const [navigation, setNavigation] = useState<PSDNavigation>(EMPTY_NAV);
  const [testStatus, setTestStatus] = useState('');

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const [reportGenerating, setReportGenerating] = useState(false);

  // Track first-time-results-complete for one-shot toast (Q5 b)
  const [resultsAlreadyComplete, setResultsAlreadyComplete] = useState(false);

  const loadData = useCallback(async () => {
    if (!workOrderDetailKey) return;
    setLoading(true);
    try {
      const data = await getPsdData(workOrderDetailKey);
      setGeneral(data.general ?? EMPTY_PSD_GENERAL);
      setSieveDetails(data.sieveDetails);
      setHydroDetails(data.hydroDetails);
      setContext(data.context);
      setNavigation(data.navigation);
      setTestStatus(data.context.testStatus || '');
      setDirty(false);
      setResultsAlreadyComplete(data.general?.computedD60 != null || data.general?.computedFinesPercent != null);
    } catch (err) {
      // 404 → render TestNotFound (clear "test removed" UX); other errors keep the toast.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setNotFound(true);
      } else {
        console.error('Failed to load PSD data:', err);
        setSnackbar({ open: true, message: 'Failed to load test data', severity: 'error' });
      }
    } finally {
      setLoading(false);
    }
  }, [workOrderDetailKey]);

  useEffect(() => { loadData(); }, [loadData]);

  // BUG-2026-0189 — warn on browser refresh / close / back with unsaved edits.
  useUnsavedChangesWarning(dirty);

  const handleGeneralChange = (field: keyof PSDGeneral, value: string | number | boolean | null) => {
    setGeneral((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };
  const handleSieveChange = (next: PSDSieveDetail[]) => { setSieveDetails(next); setDirty(true); };
  const handleHydroChange = (next: PSDHydroDetail[]) => { setHydroDetails(next); setDirty(true); };

  const doSave = async (confirmReset = false) => {
    if (!workOrderDetailKey) return;
    setSaving(true);
    try {
      const result = await savePsdData(workOrderDetailKey, general, sieveDetails, hydroDetails, confirmReset);
      const fresh = result.general ?? EMPTY_PSD_GENERAL;
      setGeneral(fresh);
      setSieveDetails(result.sieveDetails);
      setHydroDetails(result.hydroDetails);
      setContext(result.context);
      setNavigation(result.navigation);
      setTestStatus(result.context.testStatus || '');
      setDirty(false);
      const isNowComplete = fresh.computedD60 != null || (fresh.computedFinesPercent != null && fresh.computedFinesPercent > 0);
      const justBecameComplete = !resultsAlreadyComplete && isNowComplete;
      setResultsAlreadyComplete(isNowComplete);
      const msg = result.resetPerformed
        ? 'Saved — signoff reset one level'
        : justBecameComplete
          ? 'Test data saved · Results computed'
          : 'Test data saved';
      setSnackbar({ open: true, message: msg, severity: 'success' });
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { error?: string; currentStatus?: string; targetStatus?: string; fieldsToBeCleared?: string[] } } };
      if (e?.response?.status === 409 && e?.response?.data?.error === 'TEST_REQUIRES_RESET') {
        const { currentStatus = '', targetStatus, fieldsToBeCleared = [] } = e.response.data;
        const fieldList = fieldsToBeCleared.length ? fieldsToBeCleared.join(', ') : 'signoff fields';
        const isApproved = currentStatus === 'Approved';
        setConfirmDlg({
          open: true,
          title: isApproved ? 'Warning: Test has been approved' : 'Warning: Test has been reviewed',
          message: targetStatus
            ? `This test is currently "${currentStatus}". Modifying test data will drop it back to "${targetStatus}" and clear: ${fieldList}.`
            : `This test is currently "${currentStatus}". Modifying test data will reset the signoff. Continue?`,
          confirmLabel: 'Yes, Reset and Save',
          confirmColor: 'warning',
          onConfirm: () => doSave(true),
        });
        setSaving(false);
        return;
      }
      setSnackbar({ open: true, message: 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };
  const handleSave = () => doSave(false);

  // BUG-2026-0189 — one boolean-returning silent save reused by Next/Prev,
  // auto-save-on-tab, and the unsaved-changes guard.
  const autoSaveIfDirty = async (): Promise<boolean> => {
    if (!dirty || !workOrderDetailKey) return true;
    try { await savePsdData(workOrderDetailKey, general, sieveDetails, hydroDetails); setDirty(false); return true; }
    catch { setSnackbar({ open: true, message: 'Failed to auto-save', severity: 'error' }); return false; }
  };
  const requestLeave = useRegisterUnsavedGuard({ dirty, save: () => autoSaveIfDirty(), setConfirm: setConfirmDlg });
  const handleTabChange = (idx: number) => {
    void autoSaveIfDirty();
    setActiveTab(idx);
  };

  const navToWod = (wodKey: string) => {
    if (!projectId || !workOrderId) return;
    navigate(ROUTES.TEST_PARAMETERS
      .replace(':projectId', projectId)
      .replace(':workOrderId', workOrderId)
      .replace(':workOrderDetailKey', wodKey));
  };
  const handleNextSample = async () => {
    if (!navigation.nextWorkOrderDetailKey) return;
    if (!(await autoSaveIfDirty())) return;
    navToWod(navigation.nextWorkOrderDetailKey);
  };
  const handlePreviousSample = async () => {
    if (!navigation.previousWorkOrderDetailKey) return;
    if (!(await autoSaveIfDirty())) return;
    navToWod(navigation.previousWorkOrderDetailKey);
  };
  const handleCancel = () => {
    const goBack = () => {
      if (projectId && workOrderId) {
        navigate(ROUTES.WORK_ORDER_DETAIL.replace(':projectId', projectId).replace(':workOrderId', workOrderId));
      }
    };
    // BUG-2026-0189 — shared Save & Leave / Discard & Leave / Cancel dialog.
    requestLeave(goBack);
  };
  const handleGenerateReport = async () => {
    if (!workOrderDetailKey) return;
    if (!general.psdGeneralKey) {
      setSnackbar({ open: true, message: 'No test data yet. Save before generating a report.', severity: 'error' });
      return;
    }
    setReportGenerating(true);
    try {
      await downloadReport(workOrderDetailKey);
      setSnackbar({ open: true, message: 'Report downloaded', severity: 'success' });
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      setSnackbar({ open: true, message: e?.response?.data?.error || 'Report generation failed', severity: 'error' });
    } finally {
      setReportGenerating(false);
    }
  };

  const breadcrumbs = useMemo(() => [
    { label: context.clientName || '...', path: ROUTES.CLIENTS, entityType: 'client' as const },
    { label: context.projectName || context.projectId || '...', path: ROUTES.PROJECTS, entityType: 'project' as const },
    { label: context.siteName || '...',
      path: context.siteKey ? ROUTES.SITE_DETAIL.replace(':siteKey', context.siteKey) : undefined,
      entityType: 'site' as const },
    { label: context.locationName || '...',
      path: projectId ? ROUTES.LOCATIONS.replace(':projectId', projectId) : undefined,
      entityType: 'location' as const },
    { label: context.workOrderId || '...',
      path: projectId && workOrderId && !workOrderId.startsWith(':')
        ? ROUTES.WORK_ORDER_DETAIL.replace(':projectId', projectId).replace(':workOrderId', workOrderId)
        : undefined,
      entityType: 'workOrder' as const },
    { label: context.specimenRef
        ? `${context.sampleReference} (${context.specimenRef})`
        : (context.sampleReference || '...'),
      entityType: 'sample' as const },
    { label: context.testName || '...', entityType: 'test' as const },
  ], [context, projectId, workOrderId]);

  if (notFound) {
    return <TestNotFound entity="test" />;
  }
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Breadcrumb items={breadcrumbs} />
          {context.specimenDepth != null && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Specimen Depth: {context.specimenDepth} m
            </Typography>
          )}
          {testStatus && (
            <Chip label={testStatus} size="small"
              color={testStatus === 'Approved' ? 'success'
                : testStatus === 'Review Completed' ? 'info'
                : testStatus === 'In Progress' ? 'warning' : 'default'}
              variant="filled" sx={{ fontSize: '0.7rem', height: 22 }} />
          )}
        </Box>
        {previewDialog}
        <TestActionBar
          saveHidden={!(activeTab === 0 || activeTab === 1)}
          canSave={canSaveTestData}
          onSave={handleSave}
          saving={saving}
          dirty={dirty}
          onPrint={() => {
            if (activeTab !== 0) setActiveTab(0);
            setTimeout(() => window.print(), 60);
          }}
          canPrint={!reportGenerating}
          onGenerateReport={handleGenerateReport}
          onPreviewReport={openPreview}
          onReportHistory={openHistory}
          reportGenerating={reportGenerating}
          pagerLabel={`${navigation.currentIndex} / ${navigation.totalSamples}`}
        />
      </Box>

      <Box className="print-hide" sx={{ display: 'grid', gridTemplateColumns: `repeat(${TAB_DEFS.length}, 1fr)`, mb: 1.5 }}>
        {TAB_DEFS.map((tab, idx) => {
          const isActive = activeTab === idx;
          return (
            <Box key={tab.label} onClick={() => handleTabChange(idx)}
              sx={{
                height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 0.75, cursor: 'pointer', transition: 'background-color 0.2s ease',
                borderBottom: isActive ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                backgroundColor: isActive ? theme.palette.background.paper : '#F0F0EA',
                color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
                fontWeight: isActive ? 700 : 400,
                '&:hover': { backgroundColor: isActive ? theme.palette.background.paper : alpha(theme.palette.primary.main, 0.06) },
                '& .MuiSvgIcon-root': { color: isActive ? theme.palette.primary.main : theme.palette.text.secondary, fontSize: 18 },
              }}>
              {tab.icon}
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 'inherit' }}>{tab.label}</Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 0 && (
          <PSDParametersTab
            data={general}
            onChange={handleGeneralChange}
            readOnly={!canEditTestData}
            workOrderDetailKey={workOrderDetailKey}
          />
        )}
        {activeTab === 1 && (
          <PSDResultEntry
            general={general}
            sieveDetails={sieveDetails}
            hydroDetails={hydroDetails}
            onGeneralChange={handleGeneralChange}
            onSieveChange={handleSieveChange}
            onHydroChange={handleHydroChange}
            readOnly={!canEditTestData}
          />
        )}
        {activeTab === 2 && (
          <PSDAnalysis
            context={context}
            general={general}
            sieveDetails={sieveDetails}
            hydroDetails={hydroDetails}
          />
        )}
      </Box>

      {(activeTab === 0 || activeTab === 1) && (
        <Box className="print-hide">
          <TestFooterActions onSave={handleSave} onNextSample={handleNextSample}
            onPreviousSample={handlePreviousSample} onCancel={handleCancel} saving={saving}
            dirty={dirty}
            hasNext={!!navigation.nextWorkOrderDetailKey} hasPrevious={!!navigation.previousWorkOrderDetailKey}
            hideSave={!canSaveTestData}
            pagerLabel={`${navigation.currentIndex} / ${navigation.totalSamples}`} />
        </Box>
      )}

      <Snackbar open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 2500 : 4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
    </Box>
  );
};
